/**
 * Export purchase order to PDF: first page = full order, then one page per kit.
 * All prices shown are supplier cost (what we pay), not selling price.
 * Professional layout with header and reception checkboxes.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export type PdfLine = {
  description: string | null;
  supplier_sku: string;
  qty_ordered: number;
  unit_cost: number;
  currency: string;
  sources?: string[];
  /** True = bundle product (ordered as a whole) */
  is_bundle?: boolean;
  /** If set = component of this bundle (informational only); display parent SKU for clarity */
  parent_bundle_sku?: string | null;
  /** Base64 data URL for product image (when exporting PDF with images) */
  imageDataUrl?: string | null;
  /** When in a kit tab: quantity for this kit only (from snapshot or single-kit); used for "Quantité" column */
  qty_for_this_kit?: number;
};

export type PdfKit = {
  name: string;
  quantity: number;
  lines: PdfLine[];
};

export type ExportOrderPdfOptions = {
  supplierName: string;
  supplierReference?: string | null;
  orderDate?: string | null;
  expectedDate?: string | null;
  status?: string;
  notes?: string | null;
  lines: PdfLine[];
  kits?: PdfKit[];
  filename?: string;
  /** When true, add an Image column with product thumbnails (lines should have imageDataUrl) */
  withImages?: boolean;
};

const MARGIN = 20;
/** Extra top margin (mm) on each page. */
const TOP_MARGIN_MM = 10;
const PAGE_W = 210;
const PAGE_H = 297;
const HEADER_COLOR: [number, number, number] = [0.2, 0.35, 0.55];
const BORDER_COLOR = 0.85;
/** Font size for table cells (pt) – compact to save space. */
const TABLE_FONT_SIZE = 6;
/** Gray tint for bundle component rows (contenu bundle) to make them stand out. */
const BUNDLE_CONTENT_TINT: [number, number, number] = [228, 228, 235];

function formatDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return String(s);
  }
}

/** Format amount as "xx xxx,xx $" (French style: space thousands, comma decimal). */
function formatMoney(value: number): string {
  const n = Number(value);
  if (Number.isNaN(n)) return "— $";
  const [intPart, decPart] = n.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces},${decPart ?? "00"} $`;
}

/** Draw an empty wider box in the Reçu column for writing (replaces checkbox). */
function drawEmptyReceiptBox(doc: jsPDF, cellX: number, cellY: number, cellW: number, cellH: number): void {
  const pad = 1;
  const h = Math.min(5, cellH - 2);
  const w = Math.max(10, cellW - 2 * pad);
  const x = cellX + (cellW - w) / 2;
  const y = cellY + (cellH - h) / 2;
  doc.setDrawColor(0.4, 0.4, 0.4);
  doc.setLineWidth(0.25);
  doc.rect(x, y, w, h);
}

const IMAGE_CELL_SIZE_MM = 14;

/** Draw product thumbnail in a table cell; no-op if no imageDataUrl. */
function drawCellImage(
  doc: jsPDF,
  imageDataUrl: string | null | undefined,
  cellX: number,
  cellY: number,
  cellW: number,
  cellH: number
): void {
  if (!imageDataUrl || typeof imageDataUrl !== "string") return;
  const format = imageDataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
  const size = Math.min(IMAGE_CELL_SIZE_MM, cellW, cellH, 14);
  const x = cellX + (cellW - size) / 2;
  const y = cellY + (cellH - size) / 2;
  try {
    doc.addImage(imageDataUrl, format, x, y, size, size);
  } catch {
    // ignore invalid or failed image
  }
}

function addHeader(doc: jsPDF, title: string, yStart: number): number {
  let y = yStart;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("XLFlow – Bon de commande fournisseur", MARGIN, y);
  y += 6;
  doc.setFontSize(14);
  doc.text(title, MARGIN, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  return y;
}

function addOrderInfoBlock(doc: jsPDF, options: ExportOrderPdfOptions, yStart: number): number {
  let y = yStart;
  const col1 = MARGIN;
  const col2 = 100;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Fournisseur", col1, y);
  doc.setFont("helvetica", "normal");
  doc.text(options.supplierName || "—", col2, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Référence / N° PO", col1, y);
  doc.setFont("helvetica", "normal");
  doc.text(options.supplierReference || "—", col2, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Date commande", col1, y);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(options.orderDate ?? undefined), col2, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Date prévue", col1, y);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(options.expectedDate ?? undefined), col2, y);
  y += 6;
  if (options.status) {
    doc.setFont("helvetica", "bold");
    doc.text("Statut", col1, y);
    doc.setFont("helvetica", "normal");
    doc.text(options.status === "draft" ? "Brouillon" : options.status === "sent" ? "Envoyée" : options.status, col2, y);
    y += 6;
  }
  if (options.notes) {
    doc.setFont("helvetica", "bold");
    doc.text("Notes", col1, y);
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(options.notes, PAGE_W - col2 - MARGIN);
    doc.text(noteLines, col2, y);
    y += Math.max(6, noteLines.length * 5);
  }
  y += 4;
  return y;
}

const BUNDLE_NOTE_TEXT =
  "Note : Les articles listés immédiatement sous un bundle décrivent son contenu (indicatif). Ne pas les commander en double.";

export function exportOrderPdf(options: ExportOrderPdfOptions): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const filename = options.filename ?? `commande_${(options.supplierName || "fournisseur").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;

  let y = addHeader(doc, "Commande complète", TOP_MARGIN_MM);
  y = addOrderInfoBlock(doc, options, y);

  const tableWidth = PAGE_W - 2 * MARGIN;

  const typeLabel = (l: PdfLine): string => {
    if (l.is_bundle) return "Bundle";
    if (l.parent_bundle_sku) return `Contenu (${l.parent_bundle_sku})`;
    return "Article";
  };

  const withImages = options.withImages === true;
  const tableHead = withImages
    ? [["Image", "Type", "Nom", "SKU", "Qté", "Coût unit. (fourn.)", "Total", "Reçu"]]
    : [["Type", "Nom", "SKU", "Qté", "Coût unit. (fourn.)", "Total", "Reçu"]];
  const boldCell = (text: string) => ({ content: text, styles: { fontStyle: "bold" as const } });
  const body = options.lines.map((l) => {
    const total = l.qty_ordered * (l.unit_cost ?? 0);
    const typeStr = typeLabel(l);
    const nameStr = (l.description ?? "—").trim().slice(0, withImages ? 38 : 42) || "—";
    const isBold = !!l.is_bundle;
    const row = [
      isBold ? boldCell(typeStr) : typeStr,
      isBold ? boldCell(nameStr) : nameStr,
      l.supplier_sku,
      String(Math.round(Number(l.qty_ordered))),
      formatMoney(l.unit_cost ?? 0),
      formatMoney(total),
      "",
    ];
    if (withImages) row.unshift(""); // image cell
    return row;
  });

  const rowStyles: Record<number, { fillColor?: [number, number, number]; minCellHeight?: number; fontStyle?: "bold" }> = {};
  options.lines.forEach((l, i) => {
    rowStyles[i] = {
      fillColor: l.is_bundle ? [200, 220, 255] : (l.parent_bundle_sku ? BUNDLE_CONTENT_TINT : undefined),
      minCellHeight: withImages ? 18 : undefined,
      fontStyle: l.is_bundle ? "bold" : undefined,
    };
  });

  const mainColStyles: Record<number, { cellWidth: number; overflow?: "linebreak" }> = withImages
    ? {
        0: { cellWidth: 18 },
        1: { cellWidth: 24 },
        2: { cellWidth: 40, overflow: "linebreak" },
        3: { cellWidth: 22 },
        4: { cellWidth: 12 },
        5: { cellWidth: 22 },
        6: { cellWidth: 20 },
        7: { cellWidth: 12 },
      }
    : {
        0: { cellWidth: 28 },
        1: { cellWidth: 48, overflow: "linebreak" },
        2: { cellWidth: 24 },
        3: { cellWidth: 12 },
        4: { cellWidth: 24 },
        5: { cellWidth: 22 },
        6: { cellWidth: 12 },
      };
  const checkboxColIndex = withImages ? 7 : 6;

  autoTable(doc, {
    startY: y,
    head: tableHead,
    body,
    theme: "grid",
    styles: { fontSize: TABLE_FONT_SIZE },
    headStyles: { fillColor: HEADER_COLOR, textColor: 255 },
    columnStyles: mainColStyles,
    rowStyles,
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const rowIndex = data.row.index;
      const line = options.lines[rowIndex];
      const cell = data.cell;
      if (withImages && data.column.index === 0) {
        drawCellImage(doc, line?.imageDataUrl, cell.x, cell.y, cell.width, cell.height);
      } else if (data.column.index === checkboxColIndex) {
        drawEmptyReceiptBox(doc, cell.x, cell.y, cell.width, cell.height);
      }
    },
    margin: { left: MARGIN, right: MARGIN },
    tableWidth,
  });

  const lastTable = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable;
  let finalY = lastTable?.finalY ?? y + 20;
  const linesForTotal = options.lines.filter((l) => !l.parent_bundle_sku);
  const totalValue = linesForTotal.reduce((s, l) => s + l.qty_ordered * (l.unit_cost ?? 0), 0);
  finalY += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`Total commande : ${formatMoney(totalValue)}`, MARGIN, finalY);
  finalY += 8;

  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(0.4, 0.4, 0.4);
  const bundleNoteLines = doc.splitTextToSize(BUNDLE_NOTE_TEXT, tableWidth);
  doc.text(bundleNoteLines, MARGIN, finalY);
  finalY += bundleNoteLines.length * 4 + 6;
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  // One page per kit
  if (options.kits?.length) {
    for (const kit of options.kits) {
      doc.addPage();
      y = addHeader(doc, `Kit : ${kit.name}`, TOP_MARGIN_MM);
      y += 4;

      const kitTypeLabel = (line: PdfLine): string => {
        if (line.is_bundle) return "Bundle";
        if (line.parent_bundle_sku) return `Contenu (${line.parent_bundle_sku})`;
        return "Article";
      };
      const kitHead = withImages
        ? [["Image", "Type", "Nom", "SKU", "Quantité", "Coût unit. (fourn.)", "Montant", "Reçu"]]
        : [["Type", "Nom", "SKU", "Quantité", "Coût unit. (fourn.)", "Montant", "Reçu"]];
      const kitBody = kit.lines.map((l) => {
        // Prefer explicit quantity for this kit (from snapshot); else derive from total
        const qtyThisKit =
          l.qty_for_this_kit != null
            ? Math.round(l.qty_for_this_kit)
            : kit.quantity
              ? (l.is_bundle ? l.qty_ordered : Math.round(l.qty_ordered / kit.quantity))
              : Math.round(l.qty_ordered);
        const total = (l.qty_for_this_kit != null ? l.qty_for_this_kit : l.qty_ordered) * (l.unit_cost ?? 0);
        const typeStr = kitTypeLabel(l);
        const nameStr = (l.description ?? "—").trim().slice(0, withImages ? 36 : 40) || "—";
        const row = [
          l.is_bundle ? boldCell(typeStr) : typeStr,
          l.is_bundle ? boldCell(nameStr) : nameStr,
          l.supplier_sku,
          String(qtyThisKit),
          formatMoney(l.unit_cost ?? 0),
          formatMoney(total),
          "",
        ];
        if (withImages) row.unshift("");
        return row;
      });
      const kitRowStyles: Record<number, { fillColor?: [number, number, number]; minCellHeight?: number; fontStyle?: "bold" }> = {};
      kit.lines.forEach((l, i) => {
        kitRowStyles[i] = {
          fillColor: l.is_bundle ? [200, 220, 255] : l.parent_bundle_sku ? BUNDLE_CONTENT_TINT : undefined,
          minCellHeight: withImages ? 18 : undefined,
          fontStyle: l.is_bundle ? "bold" : undefined,
        };
      });
      const kitColStyles: Record<number, { cellWidth: number; overflow?: "linebreak" }> = withImages
        ? {
            0: { cellWidth: 18 },
            1: { cellWidth: 20 },
            2: { cellWidth: 46, overflow: "linebreak" },
            3: { cellWidth: 26 },
            4: { cellWidth: 14 },
            5: { cellWidth: 18 },
            6: { cellWidth: 20 },
            7: { cellWidth: 18 },
          }
        : {
            0: { cellWidth: 24 },
            1: { cellWidth: 54, overflow: "linebreak" },
            2: { cellWidth: 28 },
            3: { cellWidth: 14 },
            4: { cellWidth: 20 },
            5: { cellWidth: 22 },
            6: { cellWidth: 18 },
          };
      const kitCheckboxColIndex = withImages ? 7 : 6;

      autoTable(doc, {
        startY: y,
        head: kitHead,
        body: kitBody,
        theme: "grid",
        styles: { fontSize: TABLE_FONT_SIZE },
        headStyles: { fillColor: HEADER_COLOR, textColor: 255 },
        columnStyles: kitColStyles,
        rowStyles: kitRowStyles,
        didDrawCell: (data) => {
          if (data.section !== "body") return;
          const rowIndex = data.row.index;
          const line = kit.lines[rowIndex];
          const cell = data.cell;
          if (withImages && data.column.index === 0) {
            drawCellImage(doc, line?.imageDataUrl, cell.x, cell.y, cell.width, cell.height);
          } else if (data.column.index === kitCheckboxColIndex) {
            drawEmptyReceiptBox(doc, cell.x, cell.y, cell.width, cell.height);
          }
        },
        margin: { left: MARGIN, right: MARGIN },
        tableWidth,
      });

      const kitLastTable = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable;
      let kitFinalY = kitLastTable?.finalY ?? y + 20;
      const kitLinesForTotal = kit.lines.filter((l) => !l.parent_bundle_sku);
      const kitTotal = kitLinesForTotal.reduce(
        (s, l) => s + (l.qty_for_this_kit != null ? l.qty_for_this_kit : l.qty_ordered) * (l.unit_cost ?? 0),
        0
      );
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`Total kit : ${formatMoney(kitTotal)}`, MARGIN, kitFinalY + 6);
      kitFinalY += 8;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(0.4, 0.4, 0.4);
      const kitNoteLines = doc.splitTextToSize(BUNDLE_NOTE_TEXT, tableWidth);
      doc.text(kitNoteLines, MARGIN, kitFinalY);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
    }
  }

  doc.save(filename);
}
